import { useDeletePlanById } from "lib/api-plans";
import React, { useState, useMemo } from "react";
import {
    Badge,
    Card,
    CardHeader,
    Table,
    Row,
    Alert,
    Media,
    Pagination,
    PaginationItem,
    PaginationLink,
    Button,
} from "reactstrap";

const PlanTable = ({ plans, title, isLoading, error, tokens = [] }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getStatusBadge = (plan) => {
        if (!plan.isActive) return <Badge color="warning">Inactive</Badge>;
        const now = new Date();
        const deadline = new Date(plan.investmentDeadline);
        if (now > deadline) return <Badge color="danger">Expired</Badge>;
        return <Badge color="success">Active</Badge>;
    };

    const paginatedPlans = useMemo(() => {
        if (!plans || plans.length === 0) return [];
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return plans.slice(startIndex, endIndex);
    }, [plans, currentPage]);

    const totalPages = useMemo(() => {
        if (!plans || plans.length === 0) return 0;
        return Math.ceil(plans.length / itemsPerPage);
    }, [plans]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(
            1,
            currentPage - Math.floor(maxVisiblePages / 2)
        );
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        pages.push(
            <PaginationItem key="prev" disabled={currentPage === 1}>
                <PaginationLink
                    previous
                    onClick={() => handlePageChange(currentPage - 1)}
                />
            </PaginationItem>
        );

        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            pages.push(
                <PaginationItem key={page} active={page === currentPage}>
                    <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        // Next button
        pages.push(
            <PaginationItem key="next" disabled={currentPage === totalPages}>
                <PaginationLink
                    next
                    onClick={() => handlePageChange(currentPage + 1)}
                />
            </PaginationItem>
        );

        return (
            <div className="d-flex justify-content-center mt-3 mb-3">
                <Pagination size="sm">{pages}</Pagination>
            </div>
        );
    };

    return (
        <Card className="shadow mb-4">
            <CardHeader className="border-0">
                <Row className="align-items-center">
                    <div className="col">
                        <h3 className="mb-0">{title}</h3>
                    </div>
                    <div className="col text-right">
                        <span className="text-muted">
                            {plans?.length || 0} plan
                            {plans?.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                </Row>
            </CardHeader>

            {error && (
                <Alert color="danger" className="mx-3">
                    Error loading plans: {error.message}
                </Alert>
            )}

            <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                    <tr>
                        <th scope="col">Token</th>
                        <th scope="col">Duration</th>
                        <th scope="col">APR</th>
                        <th scope="col">Min Stake</th>
                        <th scope="col">Deadline</th>
                        <th scope="col">Stakeholders</th>
                        <th scope="col">Total Staked</th>
                        <th scope="col">Status</th>
                        <th scope="col" />
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan="9" className="text-center py-4">
                                <div
                                    className="spinner-border spinner-border-sm text-primary"
                                    role="status"
                                >
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <span className="ml-2">Loading plans...</span>
                            </td>
                        </tr>
                    ) : !plans || plans.length === 0 ? (
                        <tr>
                            <td
                                colSpan="9"
                                className="text-center py-4 text-muted"
                            >
                                No plans found
                            </td>
                        </tr>
                    ) : (
                        paginatedPlans.map((plan) => (
                            <PlanRow
                                key={plan._id}
                                plan={plan}
                                tokens={tokens}
                                formatDate={formatDate}
                                getStatusBadge={getStatusBadge}
                            />
                        ))
                    )}
                </tbody>
            </Table>

            {renderPagination()}
        </Card>
    );
};

const PlanRow = ({ plan, tokens, formatDate, getStatusBadge }) => {
    const deletePlanMutation = useDeletePlanById(plan._id);

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this plan?")) {
            deletePlanMutation.mutate();
        }
    };

    const token = tokens?.find((t) => t._id === plan.tokenId._id);

    return (
        <tr>
            <th scope="row">
                <Media className="align-items-center">
                    <Media>
                        <span className="mb-0 text-sm font-weight-bold">
                            {token && token.name && token.symbol
                                ? `${token.symbol}`
                                : "Unknown Token"}
                        </span>
                    </Media>
                </Media>
            </th>
            <td>
                <span className="font-weight-bold">{plan.durationInDays}</span>
                <br />
                <small className="text-muted">days</small>
            </td>
            <td>
                <span className="font-weight-bold text-success">
                    {plan.interestRate}%
                </span>
                <br />
                <small className="text-muted">APR</small>
            </td>
            <td>
                <span className="font-weight-bold">
                    {plan.minInvestment?.toLocaleString() || "N/A"}
                </span>
            </td>
            <td>
                <span className="text-sm">
                    {formatDate(plan.investmentDeadline)}
                </span>
            </td>
            <td>
                <div className="d-flex align-items-center">
                    <span className="mr-2">{plan.totalInvestors || 0}</span>
                    <div className="icon icon-shape bg-info text-white rounded-circle shadow-sm">
                        <i
                            className="fas fa-users"
                            style={{ fontSize: "10px" }}
                        />
                    </div>
                </div>
            </td>
            <td>
                <span className="font-weight-bold">
                    {plan.totalInvested?.toLocaleString() || "0"}
                </span>
            </td>
            <td>{getStatusBadge(plan)}</td>
            <td>
                <Button
                    color="danger"
                    onClick={handleDelete}
                    disabled={deletePlanMutation.isLoading}
                >
                    {deletePlanMutation.isLoading ? "Deleting..." : "Delete"}
                </Button>
            </td>
        </tr>
    );
};

export default PlanTable;
